import { supabase } from '../lib/supabase'
import logger from '../utils/logger'

/**
 * Fetch recent ticker events for a match.
 * @param {string} matchId
 * @returns {Promise<Array>}
 */
export async function fetchTickerEvents(matchId) {
  const { data, error } = await supabase
    .from('ticker_events')
    .select('*')
    .eq('match_id', matchId)
    .order('created_at', { ascending: false })
    .limit(50)

  if (error) throw error
  return data
}

/**
 * Insert ticker events, ignoring duplicates.
 * @param {Array} events - Array of event objects
 * @returns {Promise<Array>}
 */
export async function insertTickerEvents(events) {
  if (!events || events.length === 0) return []

  // Split into player events and match-level events (player_id is null)
  const playerEvents = events.filter((e) => e.player_id != null)
  const matchEvents = events.filter((e) => e.player_id == null)

  const results = []

  // Upsert player events with the composite unique constraint
  if (playerEvents.length > 0) {
    const { data, error } = await supabase
      .from('ticker_events')
      .upsert(playerEvents, {
        onConflict: 'game_id,event_type,hole_number,player_id',
        ignoreDuplicates: true,
      })
      .select()

    if (error) throw error
    if (data) results.push(...data)
  }

  // Match-level events: check existence first to avoid 409 console noise,
  // since partial unique indexes can't be used with onConflict.
  for (const event of matchEvents) {
    try {
      const { data: existing } = await supabase
        .from('ticker_events')
        .select('id')
        .eq('game_id', event.game_id)
        .eq('event_type', event.event_type)
        .eq('hole_number', event.hole_number)
        .is('player_id', null)
        .maybeSingle()

      if (existing) continue // already recorded

      const { data, error } = await supabase
        .from('ticker_events')
        .insert(event)
        .select()
        .single()

      if (data) results.push(data)
      if (error && error.code !== '23505') throw error
    } catch (err) {
      if (err?.code !== '23505') logger.error('Ticker insert error: ' + err?.message)
    }
  }

  return results
}
